// Brain Dump Voice Recorder - Simplified voice recording for brain dump
// Uses shared useAudioRecording hook with lighter, mobile-friendly styling

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAudioRecording } from '../../hooks/useAudioRecording';
import styles from './BrainDumpVoiceRecorder.module.css';

interface Recording {
  id: string;
  blob: Blob;
  duration: number;
  url: string;
}

interface BrainDumpVoiceRecorderProps {
  onRecordingsChange: (recordings: Recording[]) => void;
  recordings: Recording[];
  maxRecordings?: number;
}

export function BrainDumpVoiceRecorder({
  onRecordingsChange,
  recordings,
  maxRecordings = 10,
}: BrainDumpVoiceRecorderProps) {
  const {
    isRecording,
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    error: recordingError,
  } = useAudioRecording();

  const [error, setError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      recordings.forEach((r) => URL.revokeObjectURL(r.url));
    };
  }, []);

  // When a recording completes, add it to the list
  useEffect(() => {
    if (audioBlob && mimeType) {
      const url = URL.createObjectURL(audioBlob);
      onRecordingsChange([
        ...recordings,
        { id: `rec-${Date.now()}`, blob: audioBlob, duration, url },
      ]);
      reset();
    }
  }, [audioBlob, mimeType]);

  const handleStartRecording = useCallback(async () => {
    if (recordings.length >= maxRecordings) {
      setError(`Max ${maxRecordings} recordings`);
      return;
    }
    setError(null);
    await startRecording();
  }, [recordings.length, maxRecordings, startRecording]);

  const deleteRecording = useCallback(
    (id: string) => {
      const recording = recordings.find((r) => r.id === id);
      if (recording) URL.revokeObjectURL(recording.url);
      onRecordingsChange(recordings.filter((r) => r.id !== id));
    },
    [recordings, onRecordingsChange],
  );

  const togglePlayback = useCallback(
    (recording: Recording) => {
      if (playingId === recording.id) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setPlayingId(null);
      } else {
        if (audioRef.current) audioRef.current.pause();
        const audio = new Audio(recording.url);
        audioRef.current = audio;
        setPlayingId(recording.id);
        audio.onended = () => setPlayingId(null);
        audio.onerror = () => setPlayingId(null);
        audio.play();
      }
    },
    [playingId],
  );

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const displayError = error || recordingError;

  return (
    <div className={styles.recorder}>
      {/* Record button */}
      <button
        className={`${styles.recordButton} ${isRecording ? styles.recording : ''}`}
        onClick={isRecording ? stopRecording : handleStartRecording}
        disabled={recordings.length >= maxRecordings && !isRecording}
      >
        <span className={styles.recordIcon}>
          {isRecording ? '\u25A0' : '\u25CF'}
        </span>
        <span className={styles.recordLabel}>
          {isRecording
            ? `Recording ${formatTime(duration)}`
            : recordings.length > 0
              ? 'Record More'
              : 'Record Audio'}
        </span>
      </button>

      {displayError && <div className={styles.error}>{displayError}</div>}

      {/* Recordings list */}
      {recordings.length > 0 && (
        <div className={styles.list}>
          {recordings.map((recording, index) => (
            <div key={recording.id} className={styles.item}>
              <button
                className={styles.playButton}
                onClick={() => togglePlayback(recording)}
              >
                {playingId === recording.id ? '\u25A0' : '\u25B6'}
              </button>
              <span className={styles.itemLabel}>
                Recording {index + 1}
              </span>
              <span className={styles.itemDuration}>
                {formatTime(recording.duration)}
              </span>
              <button
                className={styles.deleteButton}
                onClick={() => deleteRecording(recording.id)}
                aria-label="Delete"
              >
                \u00D7
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
